import { authHeadersFor } from '../../core/impersonation';
import { authSnapshot, type AuthSnapshot } from '../ProgressReports/students';

const apiUrl = import.meta.env.REACT_APP_BAMBOO_API_URL ?? '';

export type Granularity = 'month' | 'quarter' | 'year';

export type UsageTotals = {
  teachersRostered: number;
  teachersActive: number;
  studentsRostered: number;
  studentsActive: number;
  toolboxViews: number;
  academyJoined: number;
  academyStarted: number;
  academyFinished: number;
};

export type UsageMonth = { month: string; daysLoggedIn: number; toolboxViews: number };

export type UsageCourse = { courseId: string; title: string; stepsViewed: number; steps: number; lastViewedAt: string };

export type UsageStudent = { userId: string; firstName: string; lastName: string; schoolId: string; active: boolean };

export type UsageTeacher = {
  userId: string;
  firstName: string;
  lastName: string;
  email: string;
  schoolIds: string[];
  studentsRostered: number;
  studentsActive: number;
  daysLoggedIn: number;
  toolboxViews: number;
  months: UsageMonth[];
  academy: { joinedAt?: string; coursesStarted: number; coursesFinished: number; courses: UsageCourse[] };
  students?: UsageStudent[];
};

export type UsageSchool = { schoolId: string; name: string; totals: UsageTotals; teacherIds: string[] };

export type UsageReport = {
  timeZone: string;
  periodStart: string;
  periodEnd: string;
  district: UsageTotals;
  schools: UsageSchool[];
  teachers: UsageTeacher[];
};

export type ReportWindow = { start: string; end: string; fullEnd?: string };

export type ImplementationReportResponse = {
  available: boolean;
  granularity?: Granularity;
  today?: string;
  districtName?: string;
  period?: ReportWindow;
  previous?: ReportWindow;
  current?: UsageReport | null;
  previousReport?: UsageReport | null;
};

export type ReportQuery = { granularity: Granularity; start?: string; teacher?: string; today?: string };

export class ImplementationReportError extends Error {
  status: number;
  constructor(message: string, status: number) {
    super(message);
    this.name = 'ImplementationReportError';
    this.status = status;
  }
}

export type ReportSource = (auth: AuthSnapshot, query: ReportQuery) => Promise<ImplementationReportResponse>;

function queryString(query: ReportQuery): string {
  const params = new URLSearchParams({ granularity: query.granularity });
  if (query.start) params.set('start', query.start);
  if (query.teacher) params.set('teacher', query.teacher);
  if (query.today) params.set('today', query.today);
  return `?${params.toString()}`;
}

export async function fetchImplementationReport(auth?: AuthSnapshot, query: ReportQuery = { granularity: 'quarter' }): Promise<ImplementationReportResponse> {
  const snapshot = auth ?? (await authSnapshot());
  if (snapshot.token === undefined) throw new ImplementationReportError('no session token', 0);
  const response = await fetch(`${apiUrl}/implementation-report${queryString(query)}`, {
    headers: authHeadersFor(snapshot.token, snapshot.target),
  });
  if (!response.ok) {
    throw new ImplementationReportError(`implementation report fetch failed: ${response.status}`, response.status);
  }
  return (await response.json()) as ImplementationReportResponse;
}
